import { Controller } from "@hotwired/stimulus"
import { createControlChannelSubscription } from "../channels/control_channel"

/**
 * Stimulus controller for data export functionality.
 * Handles ControlChannel subscription for export completion notifications
 * and manages dynamic recipient field additions/removals.
 *
 * Uses the singleton ActionCable consumer via createControlChannelSubscription helper,
 * ensuring proper lifecycle management (automatic cleanup on disconnect).
 *
 * @class DataExportController
 * @extends Controller
 */
export default class extends Controller {
  static values = {
    surveyId: Number
  }

  static targets = ["recipients"]

  /**
   * Called when the controller is connected to the DOM.
   * Sets up the ControlChannel subscription for export completion notifications
   * and initializes the recipient counter (if recipients target exists).
   */
  connect() {
    // Recipients target is optional - only exists in the modal
    if (this.hasRecipientsTarget) {
      this.recipientCount = this.recipientsTarget.children.length
    } else {
      this.recipientCount = 0
    }
    this.setupControlChannel()
  }

  /**
   * Called when the controller is disconnected from the DOM.
   * Cleans up the ControlChannel subscription.
   */
  disconnect() {
    if (this.controlSub) {
      try { this.controlSub.unsubscribe() } catch (_) {}
      this.controlSub = null
    }
  }

  /**
   * Set up the ControlChannel subscription with callbacks for data_export events.
   */
  setupControlChannel() {
    if (!this.surveyIdValue) {
      console.warn("DataExport: No survey ID provided")
      return
    }

    this.controlSub = createControlChannelSubscription(
      this.surveyIdValue,
      null, // userId not needed for data export view
      {
        onDataExport: (data) => this.handleDataExport(data),
        onPptxExport: (data) => this.handlePptxExport(data)
      }
    )
  }

  /**
   * Handle data_export completion events from ControlChannel.
   * Refreshes the Turbo frame to show the completed export or shows error.
   *
   * @param {Object} data - The data export event data
   * @param {string} data.status - The export status ('completed' or 'failed')
   * @param {number} data.data_export_id - The ID of the export
   */
  handleDataExport(data) {
    if (data.status === 'completed') {
      this.refreshExportsList()
    } else if (data.status === 'failed') {
      this.showError('Data export failed. Please try again.')
      this.refreshExportsList()
    }
  }

  /**
   * Handle pptx_export completion events from ControlChannel.
   * Refreshes the Turbo frame to show the completed export or shows error.
   *
   * @param {Object} data - The PowerPoint export event data
   * @param {string} data.status - The export status ('completed' or 'failed')
   * @param {number} data.export_id - The ID of the export
   */
  handlePptxExport(data) {
    if (data.status === 'completed') {
      this.refreshExportsList()
    } else if (data.status === 'failed') {
      this.showError('PowerPoint export failed. Please try again.')
      this.refreshExportsList()
    }
  }

  /**
   * Show an error message to the user via flash.
   * Uses safe DOM methods to avoid XSS.
   * @param {string} message - The error message to display
   */
  showError(message) {
    const flashContainer = document.getElementById('flash') || document.querySelector('.flash-messages')
    if (flashContainer) {
      // Clear existing content
      flashContainer.textContent = ''

      // Create alert elements safely
      const alert = document.createElement('div')
      alert.className = 'alert alert-danger alert-dismissible fade show'
      alert.setAttribute('role', 'alert')
      alert.textContent = message

      const closeBtn = document.createElement('button')
      closeBtn.type = 'button'
      closeBtn.className = 'close'
      closeBtn.setAttribute('data-dismiss', 'alert')
      closeBtn.setAttribute('aria-label', 'Close')

      const closeIcon = document.createElement('span')
      closeIcon.setAttribute('aria-hidden', 'true')
      closeIcon.textContent = '×'

      closeBtn.appendChild(closeIcon)
      alert.appendChild(closeBtn)
      flashContainer.appendChild(alert)
    } else {
      alert(message)
    }
  }

  /**
   * Refresh the exports list using Turbo.
   * Uses Turbo.visit with replace action to update without adding to history.
   */
  refreshExportsList() {
    // Use Turbo.visit with replace to avoid history pollution
    if (typeof Turbo !== 'undefined') {
      Turbo.visit(window.location.href, { action: 'replace' })
    } else {
      window.location.reload()
    }
  }

  /**
   * Add a new recipient field to the form.
   * Creates a new form group with email input and delete button.
   *
   * @param {Event} event - The click event
   */
  addRecipient(event) {
    event.preventDefault()

    this.recipientCount++
    const nextLevel = this.recipientCount

    const recipientHtml = `
      <div class="form-group p-3 border rounded" id="recipient-${nextLevel}" data-data-export-target="recipient">
        <div class="d-flex justify-content-between">
          <label class="m-0" for="data_export[recipients][]"></label>
        </div>
        <div class="row">
          <div class="col">
            <input name="data_export[recipients][]" id="recipient-${nextLevel}" data-level="${nextLevel}" class="form-control rounded mr-3" placeholder="user@example.com" />
          </div>
          <div class="col-auto d-flex align-items-center">
            <a class="text-danger" data-confirm="Are you sure you want to remove this recipient?" data-action="click->data-export#removeRecipient" href="#">
              <i class="fa fa-times"></i> Delete Recipient
            </a>
          </div>
        </div>
      </div>
    `

    this.recipientsTarget.insertAdjacentHTML('beforeend', recipientHtml)
  }

  /**
   * Remove a recipient field from the form.
   * Removes the parent form-group containing the recipient input.
   *
   * @param {Event} event - The click event
   */
  removeRecipient(event) {
    event.preventDefault()

    const recipientGroup = event.target.closest('.form-group')
    if (recipientGroup) {
      recipientGroup.remove()
    }
  }
}
